import type { LostReason } from '@norum/contracts';
import type { Tone } from '@/ui/primitives';
import type { WireConfidence, WireContactStanding, WireIndexEntry, WireMarketRow, WireProvenance } from '@/api/wire';

/**
 * How a market's state reads, once, for every prospecting screen. A stage is
 * the platform's word; go and not now are a person's, and the chip says
 * which a stage is until someone has decided.
 */

export function stageChip(stage: WireMarketRow['stage']): { label: string; tone: Tone } {
  switch (stage) {
    case 'go':
      return { label: 'GO', tone: 'accent' };
    case 'not_now':
      return { label: 'NOT NOW', tone: 'danger' };
    case 'deep':
      return { label: 'DEEP SCAN', tone: 'warning' };
    case 'screened':
      return { label: 'SCREENED', tone: 'neutral' };
    default:
      return { label: 'NOT RESEARCHED', tone: 'neutral' };
  }
}

export const STAGE_WORD: Record<NonNullable<WireMarketRow['stage']>, string> = {
  go: 'Go',
  not_now: 'Not now',
  deep: 'Deep scan',
  screened: 'Screened',
};

/** Whether anyone there may be written to — the ledger's markets file, not research (ADR-0080). */
export function contactWords(c: WireContactStanding): string {
  if (!c.declared) return 'Not in the markets file';
  if (!c.enabled) return 'Declared, switched off';
  return 'Switched on';
}

export const PROVENANCE_WORD: Record<WireProvenance, string> = {
  sourced: 'sourced',
  inference: 'inference',
  judgement: 'judgement',
  unverified: 'unverified — to be read back',
  estimate: 'our estimate',
  not_found: 'looked for, not found',
};

export const CONFIDENCE_WORD: Record<WireConfidence, string> = {
  high: 'high confidence',
  medium: 'medium confidence',
  low: 'low confidence',
};

/** A product's code in words: black_garlic → Black garlic. */
export const productName = (code: string): string => code.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** "25 Sep", from an ISO date. */
export const shortDate = (iso: string | null | undefined): string | null =>
  iso ? new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : null;

export const eur = (n: number): string => `€${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** A lost reason and a reply's objection are the same closed set (R9, R10), read the same way everywhere. */
export const LOST_WORDS: Record<LostReason, string> = {
  price: 'Price',
  already_supplied: 'Already supplied',
  not_now: 'Not now',
  wrong_person: 'Wrong person',
  other: 'Something else',
};

/**
 * A place on Google Maps by its id: Google lets us keep the id and nothing
 * else of a place (ADR-0129), so its name is read there, live, by a person.
 */
export function googleMapsUrl(placeId: string): string {
  return `https://www.google.com/maps/search/?api=1&query=place&query_place_id=${encodeURIComponent(placeId)}`;
}

/** What a row of our index is called: its name or site once read, else what it is waiting on. */
export function indexEntryName(e: WireIndexEntry): string {
  if (e.kind === 'person') return e.shredded ? 'A person, shredded' : 'A person — sealed';
  if (e.name) return e.name;
  if (e.stage === 'discovered') return 'Name not written down yet';
  if (e.stage === 'excluded') return 'Taken off the list before it was named';
  // Only a dry run indexes a place without making it a prospect.
  if (!e.prospectId) return 'Seen in a dry run, not taken on';
  return 'Name not written down yet';
}
